// Renders a type=11 question as a table. Two modes:
//
// 1. RESULT MODE (BURNER_TYPE only): rows are produced by Flame Cell Sizing.
//    User picks a row via the SELECT_HOUSING radio column.
//
// 2. EDIT MODE (everything else — inlet SIZE_TABLE, IN, UP, ORIGIN, ...):
//    Always render `trows` rows (typically 1) of editable text cells. Each
//    cell binds to answers[`${qCode}/${col}_${row}`] so CPQ scripts that
//    read those keys see the user's input.

import { Fragment, useEffect, useMemo } from "react";
import ComboBox from "./ComboBox";
import type { Answer, AnswerState, Question, TableState } from "../types";
import { useAnswers } from "../state/AnswersContext";
import { stripHtml } from "../util/text";
import { evalRule, isHidden } from "../util/rule";
import { housingHeightKeys, isMappedHeight } from "../util/housingHeight";
import { MIRRORED_TITLE } from "../util/prediction";
import { originForPanel } from "../util/boxOrigin";
import { PredictedInput } from "./PredictedText";

// Grey = still showing the predicted value (see util/prediction).
const predCls = (on: boolean) => (on ? " is-predicted" : "");

// Inline "this height won't map" message shown under a Height cell.
function HeightError({ value }: { value: string }) {
  return (
    <div
      role="alert"
      style={{ color: "#b91c1c", fontSize: "11px", lineHeight: 1.3, marginTop: 2, maxWidth: 160 }}
    >
      “{value}” isn’t a valid housing height — pick a listed size.
    </div>
  );
}

type Props = { question: Question };

// Per-table column hiding. Some BURNER_TYPE columns (DIMENSION_HEIGHT) are
// used internally by downstream operations but shouldn't display to the user.
const HIDDEN_COLUMNS_BY_TABLE: Record<string, ReadonlySet<string>> = {
  BURNER_TYPE: new Set(["DIMENSION_HEIGHT"]),
};

// Columns forced read-only regardless of their CPQ parsed_ronly_rule. Unlike a
// rule-locked cell (which is blanked to "None"), these stay disabled but keep
// showing their actual value — e.g. INLET_INSERT main/pilot are always "None".
const FORCE_READONLY_BY_TABLE: Record<string, ReadonlySet<string>> = {
  INLET_INSERT: new Set(["MAIN", "PILOT"]),
};

// Cells that follow their CPQ parsed_ronly_rule for enable/disable, but KEEP
// showing their real value when locked (never blanked to "None"). The Inlet
// Housing Position MAIN cell is computed by the Set Housing Position op only when
// the flange position dictates it (offset -> Match Offset, centered -> Standard)
// or on Box-Type (empty) — locked + shown then — and is USER-EDITABLE otherwise.
const LOCK_KEEPS_VALUE_BY_TABLE: Record<string, ReadonlySet<string>> = {
  MATCH_OFFSET_POSITION: new Set(["MAIN"]),
};
const EMPTY_COL_SET: ReadonlySet<string> = new Set();

const RESULT_TABLES = new Set(["BURNER_TYPE"]);

// When ORDER_TYPE is a replacement (Flameco or Other) or a New Build - BYOB,
// the Housing Options table is not generated by Flame Cell Sizing — instead the
// user configures a single housing row by hand. This is the parsed form of the
// CPQ rule ORDER_TYPE/FLAMECO_REPLACEMENT # ORDER_TYPE/OTHER_REPLACEMENT # BYOB
// and matches the gate on the NOT_NEW_BUILD (op 414) and CLEAR_QTABLE (op 413)
// operations, both of which already group BYOB with the replacement order types.
const REPLACEMENT_RULE =
  '(__["ORDER_TYPE/FLAMECO_REPLACEMENT"])||(__["ORDER_TYPE/OTHER_REPLACEMENT"])||(__["ORDER_TYPE/BYOB"])';

// Allowed flame-cell quantities per housing type, mirroring the BURNERS
// `allowed` arrays in the FLAME_CELL_SIZING operation: O-Type and Hinge Type
// take a single cell; Box-Type takes two or three.
const CELLS_BY_HOUSING: Record<string, string[]> = {
  "O-Type": ["1"],
  "Hinge Type": ["1"],
  "Box-Type": ["2", "3"],
};

export default function TableWidget({ question }: Props) {
  const qCode = question.custrecord_cpqc_question_code;
  const { tables, selectedRows, selectRow, answers, setAnswer, extraAuxColumns, mappingSpec, configMode } =
    useAnswers();
  const hiddenCols = HIDDEN_COLUMNS_BY_TABLE[qCode] ?? new Set<string>();

  // Finite set of Housing-Height values the encoder can map (DIMENSIONS_HEIGHT
  // token map). Used to flag a typed Height with no mapping. null when the spec
  // can't be read — the check then no-ops.
  const heightKeys = useMemo(() => housingHeightKeys(mappingSpec), [mappingSpec]);

  const cols = useMemo(() => {
    const base = [...question._answers]
      .filter((a) => !hiddenCols.has(a.custrecord_cpqc_answer_code))
      .sort(
        (a, b) =>
          Number(a.custrecord_cpqc_answer_sequence || 0) -
          Number(b.custrecord_cpqc_answer_sequence || 0)
      );
    // Inlet tables (those with Aux columns) gain user-added Aux 4, 5, … columns,
    // cloned from the last built-in Aux. Always editable; their values overflow
    // to custiteminlet_connection_notes on submit.
    const auxAnswers = base.filter((a) =>
      /^AUX_\d+$/.test(a.custrecord_cpqc_answer_code)
    );
    if (extraAuxColumns <= 0 || auxAnswers.length === 0) return base;
    const maxN = Math.max(
      ...auxAnswers.map((a) => Number(/^AUX_(\d+)$/.exec(a.custrecord_cpqc_answer_code)![1]))
    );
    const template = auxAnswers.find(
      (a) => a.custrecord_cpqc_answer_code === `AUX_${maxN}`
    )!;
    const extras = [];
    for (let n = maxN + 1; n <= maxN + extraAuxColumns; n++) {
      extras.push({
        ...template,
        id: `extra-${qCode}-AUX_${n}`,
        custrecord_cpqc_answer_code: `AUX_${n}`,
        custrecord_cpqc_answer_label_html: `Aux ${n}`,
        name: `Aux ${n}`,
        custrecord_cpqc_answer_sequence: String(
          Number(template.custrecord_cpqc_answer_sequence || 0) + n
        ),
        custrecord_cpqc_answer_hide_rule: "",
        custrecord_cpqc_answer_parsed_hide_rule: "",
        // Carry the template's readonly rule over, re-pointed at this Aux index,
        // so the new columns behave exactly like Aux 1-3: editable on SIZE_TABLE,
        // and locked elsewhere until this connection's size is set (≠ None).
        custrecord_cpqc_answer_parsed_ronly_rule: String(
          template.custrecord_cpqc_answer_parsed_ronly_rule || ""
        ).replace(new RegExp(`AUX_${maxN}`, "g"), `AUX_${n}`),
      });
    }
    return [...base, ...extras];
  }, [question, hiddenCols, qCode, extraAuxColumns]);

  if (RESULT_TABLES.has(qCode)) {
    // Replacement order types (Flameco / Other) configure a single housing row
    // by hand instead of generating options via Flame Cell Sizing.
    if (evalRule(REPLACEMENT_RULE, answers)) {
      return (
        <HousingReplacementTable
          qCode={qCode}
          cols={cols}
          answers={answers}
          allTables={tables}
          heightKeys={heightKeys}
          onCellChange={(col, val) => {
            setAnswer(qCode, `${col}_1`, val);
            // Keep Flame Cell Quantity valid when the housing type changes:
            // O-Type / Hinge Type force 1; switching to Box-Type clears a now-
            // invalid "1" so the user picks 2 or 3.
            if (col === "BURNER_NAME") {
              const allowed = CELLS_BY_HOUSING[val];
              const fcq = String(answers[`${qCode}/FLAME_CELL_QUANTITY_1`] ?? "");
              if (allowed && !allowed.includes(fcq)) {
                setAnswer(
                  qCode,
                  "FLAME_CELL_QUANTITY_1",
                  allowed.length === 1 ? allowed[0] : ""
                );
              }
            }
          }}
        />
      );
    }
    return (
      <ResultTable
        qCode={qCode}
        cols={cols}
        rows={tables[qCode] ?? []}
        selectedIdx={selectedRows[qCode] ?? -1}
        allTables={tables}
        answers={answers}
        heightKeys={heightKeys}
        onSelect={(i) => selectRow(qCode, i)}
        onCellChange={(col, row1, val) => setAnswer(qCode, `${col}_${row1}`, val)}
      />
    );
  }

  const trows = Math.max(1, Number(question.custrecord_cpqc_question_trows || 1));

  // Per-column readonly evaluation using the answer's parsed_ronly_rule.
  // This captures CPQ-native rules like:
  //   MATCH_OFFSET_POSITION AUX_1 readonly when (__["SIZE_TABLE/AUX_1_1"]=="None")
  // — applies across every question that defines such a rule, not just AUX.
  const forceReadonly = FORCE_READONLY_BY_TABLE[qCode] ?? new Set<string>();
  const keepsValueWhenLocked = new Set(LOCK_KEEPS_VALUE_BY_TABLE[qCode] ?? EMPTY_COL_SET);
  const lockedCols = new Set<string>();
  for (const c of cols) {
    const code = c.custrecord_cpqc_answer_code;
    // Force-readonly cells are handled separately and must keep their real
    // value, so don't let the rule blank them to "None".
    if (forceReadonly.has(code)) continue;
    const rule = c.custrecord_cpqc_answer_parsed_ronly_rule as string;
    if (rule && evalRule(rule, answers)) {
      lockedCols.add(code);
    }
  }
  // Inlet Housing Position MAIN is USER-EDITABLE. The Set Housing Position op
  // supplies a flange-based default (Offset->Match Offset, Centered->Standard,
  // Box->empty) that a manual pick OVERRIDES (the op guards on MATCH_OFFSET_MEMORY
  // and only re-defaults when the flange value itself changes). Its native ronly
  // rule also locks it whenever the flange is centered/offset — which is always —
  // so ignore that here and lock MAIN only when there is no main inlet (SIZE None).
  if (qCode === "MATCH_OFFSET_POSITION") {
    lockedCols.delete("MAIN");
    const mainSize = String(answers["SIZE_TABLE/MAIN_1"] ?? "").trim().toLowerCase();
    if (mainSize === "" || mainSize === "none") lockedCols.add("MAIN");
  }
  // From-Center mode: the FROM_CENTER Left/Right cells are readonly-locked on any
  // NON-firetube inlet by their CPQ rule — but that's exactly where the user now
  // configures them. Re-derive the locks so only UNSIZED inlets stay locked
  // (sized firetube OR from-center inlets are editable). Only affects FROM_CENTER.
  if (qCode === "FROM_CENTER" && answers["FROM_CENTER_MODE/ON"] === true) {
    lockedCols.clear();
    for (const c of cols) {
      const code = c.custrecord_cpqc_answer_code;
      const sz = String(answers[`SIZE_TABLE/${code}_1`] ?? "").trim().toLowerCase();
      if (sz === "" || sz === "none") lockedCols.add(code);
    }
  }
  // Box-Type Origin on a Left or Right panel is set from the panel (operation
  // cpq2-ndb-box-origin, util/boxOrigin): locked, showing its value. An unsized inlet is
  // already rule-locked and stays blanked to "None".
  const originFixes: Array<[string, string]> = [];
  if (qCode === "ORIGIN") {
    for (const c of cols) {
      const code = c.custrecord_cpqc_answer_code;
      const want = originForPanel(answers[`PANEL/${code}_1`]);
      if (!want || lockedCols.has(code)) continue;
      lockedCols.add(code);
      keepsValueWhenLocked.add(code);
      // A New Config shows what the rule says even when a prediction or a Favorite was
      // applied verbatim, without waiting for the next edit to run the operations. An item
      // being edited may keep its saved origin, which only the operation knows.
      if (configMode === "new" && answers[`ORIGIN/${code}_1`] !== want) originFixes.push([code, want]);
    }
  }
  useEffect(() => {
    for (const [code, want] of originFixes) setAnswer("ORIGIN", `${code}_1`, want, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originFixes.map((f) => f.join("=")).join("|")]);

  // A From Center direction the inlet's orientation no longer allows (Up on a 0-degree inlet,
  // Left on a 90) is cleared, so the cell asks again rather than keeping a value it cannot
  // show (see fromCenterChoices). A New Config only, as above: an item being edited keeps what
  // it was saved with until someone changes it.
  const fromCenterFixes: string[] = [];
  if (qCode === "FROM_CENTER" && configMode === "new") {
    for (const c of cols) {
      const code = c.custrecord_cpqc_answer_code;
      if (lockedCols.has(code)) continue;
      const allowed = fromCenterChoices(answers[`ORIENTATION_TABLE/${code}_1`]);
      const cur = String(answers[`FROM_CENTER/${code}_1`] ?? "");
      if (allowed && cur !== "" && cur !== "None" && !allowed.includes(cur)) fromCenterFixes.push(code);
    }
  }
  useEffect(() => {
    for (const code of fromCenterFixes) setAnswer("FROM_CENTER", `${code}_1`, "", false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromCenterFixes.join("|")]);

  // When a column becomes readonly, force its stored value to "None" so that
  // downstream scripts and rules see the canonical empty value rather than a
  // stale user choice. Only fires when value differs (no infinite loop).
  useEffect(() => {
    if (qCode === "SIZE_TABLE") return; // SIZE_TABLE is source of truth
    for (let row1 = 1; row1 <= trows; row1++) {
      for (const colCode of lockedCols) {
        // Keep-value cells (Inlet Housing Position MAIN, a box Origin set from its panel)
        // stay locked but show their real computed value — never blank them to "None".
        if (keepsValueWhenLocked.has(colCode)) continue;
        const key = `${qCode}/${colCode}_${row1}`;
        if (answers[key] !== "None") {
          setAnswer(qCode, `${colCode}_${row1}`, "None", false);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qCode, trows, Array.from(lockedCols).sort().join("|"), Array.from(keepsValueWhenLocked).sort().join("|")]);

  return (
    <EditableTable
      qCode={qCode}
      cols={cols}
      trows={trows}
      answers={answers}
      lockedCols={lockedCols}
      keepsValueWhenLocked={keepsValueWhenLocked}
      forceReadonly={forceReadonly}
      onCellChange={(col, row1, val) => setAnswer(qCode, `${col}_${row1}`, val)}
    />
  );
}

// ---------- Result mode (BURNER_TYPE) ----------

type ResultTableProps = {
  qCode: string;
  cols: Question["_answers"];
  rows: Array<Record<string, unknown>>;
  selectedIdx: number;
  allTables: TableState;
  answers: Record<string, unknown>;
  heightKeys: ReadonlySet<string> | null;
  onSelect: (rowIdx: number) => void;
  onCellChange: (colCode: string, row1Based: number, val: string) => void;
};

function ResultTable({
  qCode,
  cols,
  rows,
  selectedIdx,
  allTables,
  answers,
  heightKeys,
  onSelect,
  onCellChange,
}: ResultTableProps) {
  const { isPredicted } = useAnswers();
  if (rows.length === 0) {
    return (
      <div className="cpq-table-empty">
        Click <strong>Generate Housing Options</strong> after filling sizing
        requirements to populate this table.
      </div>
    );
  }
  return (
    <div className="cpq-table-wrap">
      <table className="cpq-table">
        <thead>
          <tr>
            {cols.map((c) => (
              <th key={c.id}>{colLabel(c)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const isSelected = i === selectedIdx;
            const row1 = i + 1;
            // The predicted housing row stays grey until another row is picked.
            const rowPredicted = isSelected && isPredicted(`${qCode}/SELECT_HOUSING_${row1}`);
            return (
              <tr
                key={i}
                className={`${isSelected ? "is-selected" : ""}${predCls(rowPredicted)}`.trim()}
                onClick={() => onSelect(i)}
              >
                {cols.map((c) => {
                  const colCode = c.custrecord_cpqc_answer_code;
                  if (isSelectColumn(colCode)) {
                    return (
                      <td key={c.id}>
                        <input
                          type="radio"
                          name={`${qCode}-row`}
                          checked={isSelected}
                          onChange={() => onSelect(i)}
                          // One selection per click. Without this a click on the radio ALSO
                          // bubbled to the row's onClick, so the row was selected twice from
                          // the same answers: the second pass started from the answers before
                          // the first, and threw away what the first had set -- the Hood Size
                          // "Set hood size" writes once, for one (Kevin, 2026-10-22).
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                    );
                  }
                  const cellType = c.custrecord_cpqc_answer_qcell_type ?? "";
                  const resultTableId = c.custrecord_cpqc_answer_result_table ?? "";

                  // qcell_type=2 with result_table → dropdown sourced from
                  // the referenced qtable, using picklist_custopt as label
                  // template. Options are filtered by per-row context so
                  // each housing row only sees designs/heights valid for it.
                  if (cellType === "2" && resultTableId) {
                    const opts = resolveResultTableOptions(
                      resultTableId,
                      c.custrecord_cpqc_answer_picklist_custopt,
                      allTables,
                      row
                    );
                    // Per-row value lives in answers[QCODE/COL_ROW]; fall back to
                    // the row's column value (set by the script) only when the answer
                    // key is ABSENT. Presence, not truthiness: once a cell has a value
                    // (generated or user-edited) it wins over the regenerated row —
                    // including an intentional blank. Using `||` reverted a cleared
                    // Height back to the stale row value, so it appeared unchangeable.
                    const storedKey = `${qCode}/${colCode}_${row1}`;
                    const v =
                      storedKey in answers
                        ? String((answers[storedKey] as string | number | null | undefined) ?? "")
                        : String(row[colCode] ?? "");

                    // Height (DIMENSIONS) is a combobox: pick a suggested size or type a
                    // custom one. It was <input list> + <datalist> and now uses the same
                    // ComboBox as Flange to Nozzle End, because the native control filtered
                    // its own list down to the current value - so once a height was set the
                    // dropdown could not be reopened without clearing it first.
                    if (colCode === "DIMENSIONS") {
                      const badHeight = !isMappedHeight(v, heightKeys);
                      return (
                        <td key={c.id} onClick={(e) => e.stopPropagation()}>
                          <ComboBox
                            value={v}
                            options={opts.map((o) => ({ value: o.value, label: o.label }))}
                            inputClassName={`cpq-cell-input${badHeight ? " is-invalid" : ""}${predCls(isPredicted(storedKey))}`}
                            inputStyle={badHeight ? { borderColor: "#b91c1c" } : undefined}
                            ariaInvalid={badHeight || undefined}
                            replaceOnType={isPredicted(storedKey)}
                            onChange={(nv) => onCellChange(colCode, row1, nv)}
                          />
                          {badHeight && <HeightError value={v} />}
                        </td>
                      );
                    }

                    return (
                      <td key={c.id} onClick={(e) => e.stopPropagation()}>
                        <select
                          className={`cpq-input cpq-cell-input${predCls(isPredicted(storedKey))}`}
                          value={v}
                          onChange={(e) =>
                            onCellChange(colCode, row1, e.target.value)
                          }
                        >
                          <option value="" hidden></option>
                          {opts.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </td>
                    );
                  }

                  const v = row[colCode];
                  return (
                    <td key={c.id}>
                      {v === undefined || v === null ? "" : String(v)}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Resolve a result_table reference (numeric qtable id) to a filtered list of
// dropdown options, given the BURNER_TYPE row context.
//
//   DESIGN (id=19): filter column = row.BURNER_NAME (the housing type
//     header, e.g. "Hinge Type"). Only rows where that column == "T" qualify.
//   DIMENSION_HEIGHT (id=20): filter column = row.DIMENSION_HEIGHT
//     (e.g. "Hinge Type >= 26", set by FLAME_CELL_SIZING). Only rows
//     where that column == "T" qualify.
//
// `tpl` is a CPQ label template like "<p>{id}</p>" or "<p>{Size}</p>" —
// HTML stripped, then {fieldName} substituted from each qualifying row.
function resolveResultTableOptions(
  tableId: string,
  tpl: string | undefined,
  allTables: TableState,
  burnerRow: Record<string, unknown>
): Array<{ value: string; label: string }> {
  let code: string | undefined;
  let filterCol: string | undefined;
  if (tableId === "19") {
    code = "DESIGN";
    filterCol = String(burnerRow.BURNER_NAME ?? "");
  } else if (tableId === "20") {
    code = "DIMENSION_HEIGHT";
    filterCol = String(burnerRow.DIMENSION_HEIGHT ?? "");
  } else {
    return [];
  }
  if (!filterCol) return []; // no context yet

  const rows = allTables[code] ?? [];
  const template = stripHtml(tpl ?? "") || "{id}";
  return rows
    .filter((r) => String(r[filterCol] ?? "") === "T")
    .map((r) => {
      const value = String(r.id ?? r.Size ?? "");
      const label = template.replace(/\{(\w+)\}/g, (_m, field) => {
        const fv = r[field as keyof typeof r];
        return fv === undefined || fv === null ? "" : String(fv);
      });
      return { value, label: label || value };
    })
    .filter((o) => o.value !== "");
}

function colLabel(c: Answer): string {
  return (
    stripHtml(c.custrecord_cpqc_answer_label_html) ||
    c.name ||
    c.custrecord_cpqc_answer_code
  );
}

// ---------- Edit mode (inlet tables, etc.) ----------
//
// qcell_type values (per CPQ):
//   1 = read-only display
//   2 = text input
//   3 = dropdown — options come from custrecord_cpqc_answer_qc_opt (JSON array)
//   4 = selection radio (e.g. SELECT_HOUSING)

type EditableTableProps = {
  qCode: string;
  cols: Question["_answers"];
  trows: number;
  answers: AnswerState;
  lockedCols: Set<string>;
  // Locked cells that show their value instead of "None".
  keepsValueWhenLocked: ReadonlySet<string>;
  forceReadonly: ReadonlySet<string>;
  onCellChange: (colCode: string, row1Based: number, val: string) => void;
};

// Firetube UP direction: a small Up/Down toggle rendered INSIDE the header bar,
// to the right of the column name, for any column whose orientation is
// "Firetube". Styled (.cpq-updir-header) to blend into the Main/Pilot/Aux bar.
// The distance stays in the cell below; this only flips the direction from the
// firetube centerline. Stored as answers[UP_DIRECTION/<col>_<row>] (default
// "Up") — it round-trips via the config snapshot and the MAPPING encoder.
function UpDirectionHeader({
  col,
  row,
  disabled = false,
}: {
  col: string;
  row: number;
  disabled?: boolean;
}) {
  const { answers, setAnswer, isPredicted } = useAnswers();
  // The Up/Down direction is a firetube-only concept: a non-firetube inlet is
  // always "Up", so its control is shown but locked (disabled) at "Up".
  const value = disabled
    ? "Up"
    : String(answers[`UP_DIRECTION/${col}_${row}`] ?? "") || "Up";
  return (
    <select
      className={`cpq-updir-header${predCls(!disabled && isPredicted(`UP_DIRECTION/${col}_${row}`))}`}
      value={value}
      disabled={disabled}
      title={
        disabled
          ? "Only a firetube inlet can be set Down"
          : "Flip this inlet Up or Down from the firetube centerline"
      }
      onChange={(e) => setAnswer("UP_DIRECTION", `${col}_${row}`, e.target.value)}
    >
      <option value="Up">Up</option>
      <option value="Down">Down</option>
    </select>
  );
}

// From-Center IN direction (Direction 1). In From-Center mode EVERY sized inlet is
// Reference Center — there is no In/Reference-Center choice — so this is a static
// label, not a dropdown. The reactive "If inlet connection is none" op sets
// IN_DIRECTION = "Reference Center" for all sized inlets while From-Center mode is
// on, which the MAPPING encoder reads (IN_DIRECTION slots -> custitem*_direction1 = 9).
function InDirectionHeader() {
  // Rendered like the Direction 2 (Up/Down) control so it lines up in the header
  // bar, but as a static label — the `--static` modifier drops the dropdown arrow
  // and pointer since Direction 1 is always Reference Center.
  return (
    <span
      className="cpq-updir-header cpq-updir-header--static"
      title="Direction 1: Reference Center"
    >
      Reference Center
    </span>
  );
}

function EditableTable({
  qCode,
  cols,
  trows,
  answers,
  lockedCols,
  keepsValueWhenLocked,
  forceReadonly,
  onCellChange,
}: EditableTableProps) {
  // "Direction" mode: once ANY inlet is firetube, the UP row's header shows an
  // Up/Down control on every ACTIVE inlet column (None/unused columns, i.e.
  // lockedCols, show none). Only firetube columns are changeable; the rest are
  // shown locked at "Up".
  // From-Center mode (one radio for the section; firetube auto-enables it) shows,
  // on EVERY sized inlet: Direction 2 (Up/Down) in the UP header and Direction 1
  // (In / Reference Center) in the IN header. A firetube inlet's Direction 1 is
  // locked to Reference Center. Firetube is no longer a separate UI trigger.
  const fromCenterMode = answers["FROM_CENTER_MODE/ON"] === true;
  const { isPredicted, isMirrored, needsMirrorInput, predictionNote } = useAnswers();
  // Grey while predicted; marked too when the MB mirror swapped it from the other burner; and a
  // cell the mirror could not know (the middle burner's In / Up) asks for input while blank.
  const cellMark = (key: string, value: string | number) => {
    if (needsMirrorInput(key)) return " is-needs-input";
    if (value === "" || !isPredicted(key)) return "";
    return isMirrored(key) ? " is-predicted is-mirrored" : " is-predicted";
  };
  return (
    <div className="cpq-table-wrap">
      <table className="cpq-table">
        <thead>
          <tr>
            {cols.map((c) => {
              const colCode = c.custrecord_cpqc_answer_code;
              const colSize = String(answers[`SIZE_TABLE/${colCode}_1`] ?? "")
                .trim()
                .toLowerCase();
              const colSized = colSize !== "" && colSize !== "none";
              const showUpDir = qCode === "UP" && fromCenterMode && colSized;
              const showInDir = qCode === "IN" && fromCenterMode && colSized;
              return (
                <th key={c.id}>
                  {colLabel(c)}
                  {showUpDir && <UpDirectionHeader col={colCode} row={1} />}
                  {showInDir && <InDirectionHeader />}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: trows }, (_, i) => i + 1).map((row1) => (
            <tr key={row1}>
              {cols.map((c) => {
                const colCode = c.custrecord_cpqc_answer_code;
                const key = `${qCode}/${colCode}_${row1}`;
                const raw = (answers[key] as string | number | undefined) ?? "";
                const locked = lockedCols.has(colCode);
                const forced = forceReadonly.has(colCode);
                // A rule-locked cell (this connection's size is "None") shows
                // "None". An *open* UP or IN cell is editable and starts blank
                // rather than showing the stale default "None" — so an inlet that
                // opens up (firetube, or From-Center for IN) shows a blank input
                // for entry instead of the literal "None".
                const keepsValue = keepsValueWhenLocked.has(colCode);
                let value: string | number;
                if (locked && !keepsValue) value = "None";
                else if ((qCode === "UP" || (qCode === "IN" && fromCenterMode)) && (raw === "None" || raw === "")) value = "";
                else value = raw;
                const disabled = locked || forced;
                return (
                  <td key={c.id} className={disabled ? "is-locked" : ""}>
                    {renderCell(
                      c,
                      value,
                      (val) => onCellChange(colCode, row1, val),
                      disabled,
                      disabled ? "" : cellMark(key, value),
                      predictionNote(key),
                      qCode === "FROM_CENTER" ? fromCenterChoices(answers[`ORIENTATION_TABLE/${colCode}_${row1}`]) : null
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function renderCell(
  col: Question["_answers"][number],
  value: string | number,
  onChange: (val: string) => void,
  disabled = false,
  // " is-predicted", " is-predicted is-mirrored", " is-needs-input" or "".
  mark = "",
  note: { title: string; unsure: boolean } | null = null,
  // A dropdown cell offers only these of its options (fromCenterChoices); null = all of them.
  only: readonly string[] | null = null
) {
  const predicted = mark.includes("is-predicted");
  if (predicted && note?.unsure) mark += " is-unsure";
  const a11y = {
    title: mark.includes("is-mirrored") ? MIRRORED_TITLE : predicted ? note?.title : undefined,
    "aria-required": mark.includes("is-needs-input") ? true : undefined,
  };
  const cellType = (col.custrecord_cpqc_answer_qcell_type as string) ?? "";
  // qcell_type=3 with options → dropdown.
  if (cellType === "3") {
    const all = parseQcOpt(col.custrecord_cpqc_answer_qc_opt as string);
    const opts = only ? all.filter((o) => only.includes(o)) : all;
    return (
      <select
        className={`cpq-input cpq-cell-input${mark}`}
        value={String(value)}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        {...a11y}
      >
        <option value="" hidden></option>
        {opts.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    );
  }
  // qcell_type 1, 2, 4 (in edit mode), or anything else → text input.
  // Note: in BURNER_TYPE result mode, qcell_type=1 is handled separately
  // (ResultTable renders cells as static text).
  return (
    <PredictedInput
      type="text"
      className={`cpq-input cpq-cell-input${mark}`}
      predicted={predicted}
      value={String(value)}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      {...a11y}
    />
  );
}

/**
 * The From Center directions an inlet at this orientation can take (Kevin, 2026-10-22):
 * at 0 or 180 it sits on the horizontal centerline, so only Left or Right; at 90 or 270 on
 * the vertical one, so only Up or Down. Any other angle (and Firetube) keeps every option.
 */
export function fromCenterChoices(orientation: unknown): readonly string[] | null {
  const deg = String(orientation ?? "").trim();
  if (deg === "0" || deg === "180") return ["Left", "Right"];
  if (deg === "90" || deg === "270") return ["Up", "Down"];
  return null;
}

function parseQcOpt(raw: string | undefined | null): string[] {
  if (!raw) return [];
  try {
    const v = JSON.parse(raw);
    if (Array.isArray(v)) return v.map((x) => String(x));
    return [];
  } catch {
    return [];
  }
}

function isSelectColumn(code: string): boolean {
  return /^SELECT(_|$)/i.test(code);
}

// ---------- Replacement mode (ORDER_TYPE = Flameco / Other Replacement) ----------
//
// For replacement orders the Housing Options table is configured by hand as a
// single editable row rather than generated by Flame Cell Sizing. We show only
// the columns whose answer hide-rules keep them visible for a replacement,
// which resolves to exactly the five configurable columns:
//   BURNER_NAME (Housing Type), FLAME_CELL_QUANTITY (Flame Cell Quantity),
//   CELL_OD (Cell OD), DIMENSIONS (Height), DESIGN (Design).
// The NEWBUILD-only columns (CELL_OPEN_AREA, SELECT_HOUSING) and the always-
// hidden DIMENSION_HEIGHT drop out automatically.
//
// Cell values are written to the mirror answer keys `BURNER_TYPE/<COL>_1` via
// setAnswer — the same keys getValue()/NOT_NEW_BUILD read — and deliberately do
// NOT re-run operations: in Flameco Replacement mode CLEAR_QTABLE would wipe the
// table to a blank row on every run, so the UI owns this row's state.

type HousingReplacementTableProps = {
  qCode: string;
  cols: Question["_answers"];
  answers: AnswerState;
  allTables: TableState;
  heightKeys: ReadonlySet<string> | null;
  onCellChange: (colCode: string, val: string) => void;
};

function HousingReplacementTable({
  qCode,
  cols,
  answers,
  allTables,
  heightKeys,
  onCellChange,
}: HousingReplacementTableProps) {
  const { isPredicted } = useAnswers();
  // Keep only columns whose answer hide-rule leaves them visible for a
  // replacement order — yields the five configurable columns, in sequence.
  const visibleCols = cols.filter(
    (c) =>
      !isHidden(
        {
          hideRule: c.custrecord_cpqc_answer_hide_rule,
          parsedHideRule: c.custrecord_cpqc_answer_parsed_hide_rule,
        },
        answers
      )
  );

  // Synthetic row context so the Height (DIMENSIONS) and Design dropdowns can
  // resolve their result-table options from the current cell values, without
  // running the CPQ operations. The DIMENSION_HEIGHT "bucket" is derived from
  // the chosen housing type + cell OD via the same rule the operations use.
  const burnerName = String(answers[`${qCode}/BURNER_NAME_1`] ?? "");
  const cellOd = String(answers[`${qCode}/CELL_OD_1`] ?? "");
  const contextRow: Record<string, unknown> = {
    BURNER_NAME: burnerName,
    DIMENSION_HEIGHT: heightRuleText(burnerName, cellOd) ?? "",
  };

  // A BYOB Box-Type box is the customer's, so its width is typed here, right after Cell OD.
  // The Housing Validation op puts it in the model in place of Cell OD + 4 (FAH38/24-18LN:
  // 38 is the width), and a blank keeps Cell OD + 4, which the placeholder shows.
  const showBoxWidth = answers["ORDER_TYPE/BYOB"] === true && burnerName === "Box-Type";
  const odNum = parseFloat(cellOd.replace(/[^0-9.]/g, ""));
  const boxWidth = String(answers[`${qCode}/${BOX_WIDTH}_1`] ?? "");

  return (
    <div className="cpq-table-wrap">
      <table className="cpq-table">
        <thead>
          <tr>
            {visibleCols.map((c) => (
              <Fragment key={c.id}>
                <th>{colLabel(c)}</th>
                {showBoxWidth && c.custrecord_cpqc_answer_code === "CELL_OD" && <th>Width</th>}
              </Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            {visibleCols.map((c) => {
              const colCode = c.custrecord_cpqc_answer_code;
              const value = String(answers[`${qCode}/${colCode}_1`] ?? "");
              return (
                <Fragment key={c.id}>
                  <td>
                    {renderReplacementCell(
                      c,
                      value,
                      allTables,
                      contextRow,
                      heightKeys,
                      (val) => onCellChange(colCode, val),
                      value !== "" && isPredicted(`${qCode}/${colCode}_1`)
                    )}
                  </td>
                  {showBoxWidth && colCode === "CELL_OD" && (
                    <td>
                      <input
                        type="text"
                        inputMode="decimal"
                        aria-label="Width"
                        className="cpq-input cpq-cell-input"
                        placeholder={Number.isFinite(odNum) ? String(odNum + 4) : ""}
                        value={boxWidth}
                        onChange={(e) => onCellChange(BOX_WIDTH, e.target.value)}
                      />
                    </td>
                  )}
                </Fragment>
              );
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// BYOB Box-Type width, stored as BURNER_TYPE/BOX_WIDTH_1 (read by the Housing Validation op).
const BOX_WIDTH = "BOX_WIDTH";

function renderReplacementCell(
  col: Answer,
  value: string,
  allTables: TableState,
  contextRow: Record<string, unknown>,
  heightKeys: ReadonlySet<string> | null,
  onChange: (val: string) => void,
  predicted = false
) {
  const colCode = col.custrecord_cpqc_answer_code;
  const cellType = (col.custrecord_cpqc_answer_qcell_type as string) ?? "";

  // qcell_type=2 → option list sourced from a result table (DESIGN, DIMENSIONS).
  if (cellType === "2") {
    const tableId = col.custrecord_cpqc_answer_result_table ?? "";
    let opts = resolveResultTableOptions(
      tableId,
      col.custrecord_cpqc_answer_picklist_custopt,
      allTables,
      contextRow
    );
    // Fall back to every value when context-filtering yields nothing (e.g.
    // Box-Type heights — no column in DIMENSION_HEIGHT — or before a housing
    // type is chosen) so the user always has something to pick or type.
    if (opts.length === 0) opts = allResultTableValues(tableId, allTables);

    // Height (DIMENSIONS) is a combobox — pick a suggested size OR type another
    // mappable height; a typed value with no mapping is flagged. Design stays a
    // plain dropdown (its ids are a fixed list, not free-entry).
    if (colCode === "DIMENSIONS") {
      const badHeight = !isMappedHeight(value, heightKeys);
      return (
        <>
          <ComboBox
            value={value}
            options={opts.map((o) => ({ value: o.value, label: o.label }))}
            inputClassName={`cpq-cell-input${badHeight ? " is-invalid" : ""}${predCls(predicted)}`}
            inputStyle={badHeight ? { borderColor: "#b91c1c" } : undefined}
            ariaInvalid={badHeight || undefined}
            replaceOnType={predicted}
            onChange={onChange}
          />
          {badHeight && <HeightError value={value} />}
        </>
      );
    }

    return (
      <select
        className={`cpq-input cpq-cell-input${predCls(predicted)}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="" hidden></option>
        {opts.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );
  }

  // qcell_type=3 → inline picklist from qc_opt (Housing Type, Flame Cell Qty).
  if (cellType === "3") {
    let opts = parseQcOpt(col.custrecord_cpqc_answer_qc_opt as string);
    // Flame Cell Quantity is limited by housing type: O-Type / Hinge Type take a
    // single cell, Box-Type takes 2 or 3 (mirrors FLAME_CELL_SIZING).
    if (colCode === "FLAME_CELL_QUANTITY") {
      const allowed = CELLS_BY_HOUSING[String(contextRow.BURNER_NAME ?? "")];
      if (allowed) opts = allowed;
    }
    // A VALUE ALREADY CHOSEN STAYS VISIBLE EVEN IF IT IS NO LONGER OFFERED.
    //
    // A <select> whose value matches no <option> renders BLANK while quietly keeping that
    // value in state -- so the cell looks empty, the user has no idea what it holds, and
    // whatever they do next either loses it or saves it unseen. That is reachable two ways:
    // FLAME_CELL_QUANTITY narrows by housing type above, and a product can narrow a column's
    // options outright (NDB's Inlet Connection Size keeps the eight couplings that exist --
    // see ProductProfile.tableCellOptions). Neither should rewrite an older configuration by
    // sleight of hand: the size that was ordered is still the size that was ordered.
    if (value !== "" && value != null && !opts.includes(String(value))) {
      opts = [String(value), ...opts];
    }
    return (
      <select
        className={`cpq-input cpq-cell-input${predCls(predicted)}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="" hidden></option>
        {opts.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    );
  }

  // qcell_type=1 (Cell OD) or anything else → free text input.
  return (
    <PredictedInput
      type="text"
      className={`cpq-input cpq-cell-input${predCls(predicted)}`}
      predicted={predicted}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

// Mirror of the heightRuleText() helper inside the FLAME_CELL_SIZING /
// NOT_NEW_BUILD operations (operations.json). Maps a housing type + cell OD to
// the DIMENSION_HEIGHT "bucket" string used to filter the Height result table,
// so the replacement Height dropdown offers the same sizes the CPQ engine would.
function heightRuleText(typeName: string, cellOD: string): string | null {
  const t = String(typeName || "").trim();
  const od = parseFloat(String(cellOD ?? "").replace(/[^0-9.\-]/g, ""));
  if (!isFinite(od)) return null;
  if (t === "O-Type") {
    if (od <= 26) return "O-Type <= 26";
    if (od >= 28 && od <= 100) return "O-Type >= 28";
    return "";
  }
  if (t === "Hinge Type") {
    if (od <= 24) return "Hinge Type <= 24";
    if (od >= 26) return "Hinge Type >= 26";
    return "";
  }
  if (t === "Box-Type") {
    if (od <= 22) return "Box-Type <= 22";
    if (od >= 24 && od <= 36) return "Box-Type 24-36";
    return "";
  }
  return null;
}

// Unfiltered option list for a result table, used as a fallback when
// context-filtering returns nothing. DIMENSIONS (id 20) → distinct Sizes;
// DESIGN (id 19) → design ids.
function allResultTableValues(
  tableId: string,
  allTables: TableState
): Array<{ value: string; label: string }> {
  if (tableId === "20") {
    const rows = allTables["DIMENSION_HEIGHT"] ?? [];
    const seen = new Set<string>();
    const out: Array<{ value: string; label: string }> = [];
    for (const r of rows) {
      const v = String(r.Size ?? "");
      if (v && !seen.has(v)) {
        seen.add(v);
        out.push({ value: v, label: v });
      }
    }
    return out;
  }
  if (tableId === "19") {
    return (allTables["DESIGN"] ?? [])
      .map((r) => ({ value: String(r.id ?? ""), label: String(r.id ?? "") }))
      .filter((o) => o.value !== "");
  }
  return [];
}
